import { useEffect, useRef } from 'react';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useMessageStore } from '@/stores/useMessageStore.js';
import { useGisStore } from '@/stores/useGisStore.js';
import {
  createGisMessageProjectionService,
  toGisMessageSource,
  type GisMessageSource,
} from '@/domain/gis/GisMessageProjectionService.js';
import type { MessageEvent } from '@k-ucap/shared';
import type { MessageInstance } from '@k-ucap/shared';

const MAX_PROCESSED_IDS = 2000;

export const useGisRealtime = () => {
  const { messageEventRepository } = useRepositories();
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const messages = useMessageStore((s) => s.messages);

  const upsertVehicle = useGisStore((s) => s.upsertVehicle);
  const addTrackPoint = useGisStore((s) => s.addTrackPoint);
  const addAlert = useGisStore((s) => s.addAlert);
  const addWaypointUpdate = useGisStore((s) => s.addWaypointUpdate);

  const processedIdsRef = useRef<Set<string>>(new Set());
  const projectionRef = useRef(
    createGisMessageProjectionService({
      projectId: selectedProject?.id,
      environment: selectedProject?.environment,
    })
  );

  if (projectionRef.current) {
    // Update options when project changes without recreating service
    // Service currently only uses options as fallback, so recreation is acceptable
  }

  const applyUpdate = (update: ReturnType<typeof projectionRef.current.processMessageEvent>) => {
    if (!update) return;
    if (update.vehicleUpdate) {
      upsertVehicle(update.vehicleUpdate);
    }
    if (update.trackPoint) {
      addTrackPoint(update.trackPoint);
    }
    if (update.alert) {
      addAlert(update.alert);
    }
    if (update.waypointUpdate) {
      addWaypointUpdate(update.waypointUpdate);
    }
  };

  const processEvent = (event: GisMessageSource) => {
    if (!event.id || processedIdsRef.current.has(event.id)) return;
    processedIdsRef.current.add(event.id);
    if (processedIdsRef.current.size > MAX_PROCESSED_IDS) {
      const toKeep = Array.from(processedIdsRef.current).slice(-MAX_PROCESSED_IDS / 2);
      processedIdsRef.current = new Set(toKeep);
    }
    const update = projectionRef.current.processMessageEvent(event);
    applyUpdate(update);
  };

  useEffect(() => {
    projectionRef.current = createGisMessageProjectionService({
      projectId: selectedProject?.id,
      environment: selectedProject?.environment,
    });
  }, [selectedProject?.id, selectedProject?.environment]);

  useEffect(() => {
    messages.forEach((message: MessageInstance) => {
      processEvent(toGisMessageSource(message));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (!selectedProject) return;
    if (!messageEventRepository.subscribeToProject) return;

    const unsubscribe = messageEventRepository.subscribeToProject(selectedProject.id, (events: MessageEvent[]) => {
      events.forEach((event) => processEvent(event));
    });

    return () => {
      unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, messageEventRepository]);

  return {
    processedCount: processedIdsRef.current.size,
  };
};
